import React, { useState, useEffect } from 'react';
import { 
    Plus, 
    Search, 
    Edit2, 
    Trash2, 
    Loader2,
    TrendingUp,
    TrendingDown
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import MovimientoForm from './MovimientoForm';
import { useToast } from '../../../context/ToastContext';
import { useAuth } from '../../../context/AuthContext';
import { AdminService } from '../../../application/services/AdminService';
import DashboardLayout from '../../layouts/DashboardLayout';

const RendicionPage = () => {
    const { user, loading: authLoading } = useAuth();
    const [movimientos, setMovimientos] = useState([]);
    const [balance, setBalance] = useState({ totalIngresos: 0, totalEgresos: 0, balanceNeto: 0, totalDeudas: 0 });
    const [loading, setLoading] = useState(true);
    const [filterTipo, setFilterTipo] = useState('');
    
    const [showModal, setShowModal] = useState(false);
    const [selectedMovimiento, setSelectedMovimiento] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    
    const toast = useToast();

    const fetchData = async () => {
        setLoading(true);
        try {
            const filters = filterTipo ? { tipo: filterTipo } : {};
            const [movimientosData, balanceData] = await Promise.all([
                AdminService.getAllMovimientos(filters),
                AdminService.getBalance()
            ]);
            
            setMovimientos(movimientosData || []);
            setBalance(balanceData || { totalIngresos: 0, totalEgresos: 0, balanceNeto: 0, totalDeudas: 0 });
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Error al cargar datos financieros');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!authLoading) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, user, filterTipo]); 

    const handleOpenCreate = () => {
        setSelectedMovimiento(null);
        setShowModal(true);
    };

    const handleOpenEdit = (movimiento) => {
        setSelectedMovimiento(movimiento);
        setShowModal(true);
    };

    const handleDelete = (movimiento) => {
        setSelectedMovimiento(movimiento);
        setShowDeleteConfirm(true);
    };

    const handleSuccess = () => {
        setShowModal(false);
        fetchData();
    };

    const confirmDelete = async () => {
        try {
            await AdminService.deleteMovimiento(selectedMovimiento.id);
            toast.success('Registro eliminado exitosamente');
            fetchData();
        } catch (error) {
            console.error('Error deleting movimiento:', error);
            toast.error('Error al eliminar registro');
        } finally {
            setShowDeleteConfirm(false);
            setSelectedMovimiento(null);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(value || 0);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('es-EC');
    };

    const getTipoStyles = (tipo) => {
        switch (tipo) {
            case 'Ingreso':
                return 'bg-green-100 text-green-800';
            case 'Egreso':
                return 'bg-red-100 text-red-800';
            case 'Deudas por cobrar':
                return 'bg-amber-100 text-amber-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <DashboardLayout title="Rendición de Cuentas">
            <div className="space-y-6 animate-fade-in">
            
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Libro Contable</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Administración de ingresos, egresos y deudas por cobrar.
                        </p>
                    </div>
                    <Button onClick={handleOpenCreate} className="flex items-center gap-2 bg-blue-600 shadow-sm hover:bg-blue-700">
                        <Plus className="w-4 h-4" />
                        Nuevo Registro
                    </Button>
                </div>

                {/* Balance Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center gap-4">
                        <div className="p-3 bg-green-100 rounded-full">
                            <TrendingUp className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Total Ingresos</p>
                            <p className="text-xl font-bold text-green-600">{formatCurrency(balance.totalIngresos)}</p>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center gap-4">
                        <div className="p-3 bg-red-100 rounded-full">
                            <TrendingDown className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Total Egresos</p>
                            <p className="text-xl font-bold text-red-600">{formatCurrency(balance.totalEgresos)}</p>
                        </div>
                    </div>
                    <div className={`bg-white p-4 rounded-lg shadow-sm border-2 ${balance.balanceNeto >= 0 ? 'border-green-300' : 'border-red-300'} flex items-center gap-4`}>
                        <div className={`p-3 ${balance.balanceNeto >= 0 ? 'bg-green-100' : 'bg-red-100'} rounded-full`}>
                            {balance.balanceNeto >= 0 ? <TrendingUp className="w-6 h-6 text-green-600" /> : <TrendingDown className="w-6 h-6 text-red-600" />}
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Balance Neto</p>
                            <p className={`text-xl font-bold ${balance.balanceNeto >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(balance.balanceNeto)}</p>
                        </div>
                    </div>
                </div>

                {/* Filtro */}
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center gap-4">
                    <Search className="w-5 h-5 text-gray-400" />
                    <select
                        value={filterTipo}
                        onChange={(e) => setFilterTipo(e.target.value)}
                        className="flex-1 outline-none text-gray-600 bg-transparent cursor-pointer"
                    >
                        <option value="">Todos los movimientos</option>
                        <option value="Ingreso">Solo Ingresos</option>
                        <option value="Egreso">Solo Egresos</option>
                    </select>
                </div>

                {/* Tabla */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase text-gray-500 font-semibold">
                                    <th className="px-4 py-3">Tipo</th>
                                    <th className="px-4 py-3">Descripción</th>
                                    <th className="px-4 py-3 text-right">Cantidad</th>
                                    <th className="px-4 py-3 text-right">P. Unitario</th>
                                    <th className="px-4 py-3 text-right">Total</th>
                                    <th className="px-4 py-3">Fecha</th>
                                    <th className="px-4 py-3 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                                            <div className="flex justify-center items-center">
                                                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                                                Cargando registros...
                                            </div>
                                        </td>
                                    </tr>
                                ) : movimientos.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                                            No se encontraron registros
                                        </td>
                                    </tr>
                                ) : (
                                    movimientos.map((mov) => (
                                        <tr key={mov.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTipoStyles(mov.tipo)}`}>
                                                    {mov.tipo}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">{mov.descripcion || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600 text-right">{mov.cantidad}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600 text-right">{formatCurrency(mov.precioUnitario)}</td>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">{formatCurrency(mov.precioTotal)}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{formatDate(mov.fecha)}</td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button 
                                                        onClick={() => handleOpenEdit(mov)}
                                                        className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                                                        title="Editar"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDelete(mov)}
                                                        className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Modal Crear/Editar */}
                {showModal && (
                    <MovimientoForm 
                        onClose={() => setShowModal(false)}
                        onSuccess={handleSuccess}
                        initialData={selectedMovimiento}
                    />
                )}

                {/* Modal Confirmar Eliminación */}
                <Modal
                    isOpen={showDeleteConfirm}
                    onClose={() => setShowDeleteConfirm(false)}
                    title="Eliminar Registro"
                    size="sm"
                    footer={
                        <>
                            <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                                Cancelar
                            </Button>
                            <Button 
                                className="bg-red-600 hover:bg-red-700" 
                                onClick={confirmDelete}
                            >
                                Eliminar
                            </Button>
                        </>
                    }
                >
                    <div className="text-gray-600">
                        <p>¿Estás seguro de que deseas eliminar este registro de <strong>{selectedMovimiento?.tipo}</strong>?</p>
                        <p className="text-sm mt-2 text-red-500">Esta acción no se puede deshacer.</p>
                    </div>
                </Modal>

            </div>
        </DashboardLayout>
    );
};

export default RendicionPage;
